/**
 * NewsPanel — scrollable crypto news feed with sentiment indicators.
 * Data is populated by the useNews hook via the Zustand store.
 */

import { memo, useState } from 'react';
import type { NewsItem } from '@arc/shared';
import { timeAgo } from '@arc/shared';
import { useTerminalStore } from '../../stores/terminal.store.js';

// ─── Sentiment helpers ────────────────────────────────────────────────────────

function sentimentColor(s?: NewsItem['sentiment']) {
  if (s === 'bullish') return 'var(--color-buy)';
  if (s === 'bearish') return 'var(--color-sell)';
  return 'var(--color-bright)';
}

function SentimentDot({ sentiment }: { sentiment?: NewsItem['sentiment'] }) {
  if (!sentiment || sentiment === 'neutral') {
    return <span className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5 bg-[var(--color-border)] inline-block" />;
  }
  return (
    <span
      className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5 inline-block"
      style={{
        background:  sentimentColor(sentiment),
        boxShadow:   `0 0 5px ${sentimentColor(sentiment)}`,
      }}
    />
  );
}

// ─── News Item ────────────────────────────────────────────────────────────────

const NewsRow = memo(function NewsRow({ item }: { item: NewsItem }) {
  const titleColor = sentimentColor(item.sentiment);

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => {
        e.stopPropagation();
        if (item.url) {
          window.open(item.url, '_blank', 'noopener,noreferrer');
          e.preventDefault();
        }
      }}
      className="block px-3 py-2.5 border-b border-hud hover:bg-[rgba(0,191,255,0.04)] group transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <SentimentDot sentiment={item.sentiment} />
        <div className="flex-1 min-w-0">
          {/* Title — coloured by sentiment */}
          <p
            className="font-sans text-[12px] leading-snug group-hover:opacity-80 transition-opacity line-clamp-2"
            style={{ color: titleColor }}
          >
            {item.title}
          </p>
          {/* Meta row */}
          <div className="flex items-center gap-2 mt-1">
            <span
              className="font-mono text-[11px] tracking-wide"
              style={{ color: titleColor, opacity: 0.6 }}
            >
              {item.source}
            </span>
            <span className="font-mono text-[11px] text-muted">·</span>
            <span className="font-mono text-[11px] text-muted">{timeAgo(item.publishedAt)}</span>
            {item.relatedSymbols.length > 0 && (
              <>
                <span className="font-mono text-[11px] text-muted">·</span>
                <span className="font-mono text-[11px] text-arc opacity-70">
                  {item.relatedSymbols.slice(0, 3).join(' ')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </a>
  );
});

// ─── Filter Bar ───────────────────────────────────────────────────────────────

type FilterType = 'all' | 'bullish' | 'bearish';

// ─── Panel ────────────────────────────────────────────────────────────────────

export const NewsPanel = memo(function NewsPanel() {
  const news       = useTerminalStore(s => s.news);
  const [filter, setFilter] = useState<FilterType>('all');

  const filtered = filter === 'all'
    ? news
    : news.filter(item => item.sentiment === filter);

  const bullishCount = news.filter(n => n.sentiment === 'bullish').length;
  const bearishCount = news.filter(n => n.sentiment === 'bearish').length;

  return (
    <div className="panel h-full flex flex-col">
      <div className="panel-header flex-shrink-0">
        <span className="text-[11px] tracking-[2px] text-arc font-bold">INTEL FEED</span>
        <span className="badge badge-live ml-2">LIVE</span>
        <div className="ml-auto flex items-center gap-2">
          {bullishCount > 0 && (
            <span className="font-mono text-[11px] text-buy font-bold">{bullishCount}▲</span>
          )}
          {bearishCount > 0 && (
            <span className="font-mono text-[11px] text-sell font-bold">{bearishCount}▼</span>
          )}
          <span className="label-mono">{news.length}</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap border-b border-hud flex-shrink-0">
        {([
          { key: 'all',     label: 'ALL',     count: news.length,   activeColor: 'var(--color-arc)',  borderColor: 'var(--color-arc)'  },
          { key: 'bullish', label: 'BULLISH', count: bullishCount,  activeColor: 'var(--color-buy)',  borderColor: 'var(--color-buy)'  },
          { key: 'bearish', label: 'BEARISH', count: bearishCount,  activeColor: 'var(--color-sell)', borderColor: 'var(--color-sell)' },
        ] as const).map(({ key, label, count, activeColor, borderColor }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className="flex-1 min-w-fit whitespace-nowrap px-2 py-1.5 font-mono text-[11px] tracking-[2px] uppercase transition-all flex items-center justify-center gap-1"
            style={{
              color:        filter === key ? activeColor : 'var(--color-bright)',
              borderBottom: filter === key ? `2px solid ${borderColor}` : '2px solid transparent',
            }}
          >
            {label}
            <span
              className="text-[11px] opacity-70 tabular-nums"
              style={{ color: filter === key ? activeColor : 'var(--color-bright)' }}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* News list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-arc text-[20px] opacity-20 mb-2">◈</div>
              <span className="font-mono text-[11px] text-muted">
                {news.length === 0 ? 'AWAITING FEED…' : 'NO MATCHING ITEMS'}
              </span>
            </div>
          </div>
        ) : (
          filtered.map(item => <NewsRow key={item.id} item={item} />)
        )}
      </div>
    </div>
  );
});
